/**
 * ツールバー（FR-UI-01〜03）。
 * 操作子はすべて実 <button>／<input> で作り、状態は DOM 属性に現れる（canvas の中に隠さない）。
 */
import { cls } from '../brand';
import type { DisposeRegistry } from '../core/dispose-registry';
import type { UiOptions } from '../core/options';
import type { ViewerState } from '../core/viewer';
import type { Translate } from '../i18n/i18n';
import type { ViewerApi } from './api';
import { ICONS } from './icons';

/** 追加ボタンの定義（利用者拡張・FR-UI-03）。 */
export interface ControlDef {
  id: string;
  label: string;
  /**
   * アイコン。**図形は Node で渡す**（呼び出し側が組んだ DOM をそのまま置く）。
   * 文字列は HTML として解釈せず 1 つの文字列として表示する（絵文字・記号を想定）。
   * 省略時はラベルの先頭文字を表示する。
   */
  icon?: Node | string;
  onClick(api: ViewerApi): void;
}

/** Node かどうかを document をまたいでも判定できる形で見る（iframe 越しの Node も通す）。 */
function isNode(value: unknown): value is Node {
  return typeof value === 'object' && value !== null && typeof (value as Node).nodeType === 'number';
}

export class Toolbar {
  readonly el: HTMLDivElement;

  private readonly buttons = new Map<string, HTMLButtonElement>();
  private readonly pageInput: HTMLInputElement;
  private readonly pageTotal: HTMLSpanElement;
  /** いいね件数の表示（`ui.comments` が偽なら作らない）。 */
  private likeCount: HTMLSpanElement | null = null;

  constructor(
    private readonly doc: Document,
    private readonly registry: DisposeRegistry,
    private readonly api: ViewerApi,
    private readonly translate: Translate,
    ui: UiOptions,
    private readonly onTogglePanel: (id: string) => void,
  ) {
    this.el = doc.createElement('div');
    this.el.className = cls('toolbar');
    this.el.setAttribute('role', 'toolbar');
    this.el.setAttribute('aria-label', translate('toolbar.label'));

    const nav = this.group();
    nav.append(
      this.button('first', () => this.api.first()),
      this.button('prev', () => this.api.prev()),
    );

    const page = this.group();
    page.className += ` ${cls('toolbar-page')}`;
    this.pageInput = this.createPageInput();
    this.pageTotal = doc.createElement('span');
    this.pageTotal.className = cls('page-total');
    page.append(this.pageInput, this.pageTotal);

    const nav2 = this.group();
    nav2.append(
      this.button('next', () => this.api.next()),
      this.button('last', () => this.api.last()),
    );

    const zoom = this.group();
    zoom.append(
      this.button('zoomOut', () => this.api.zoomOut()),
      this.button('resetZoom', () => this.api.resetZoom()),
      this.button('zoomIn', () => this.api.zoomIn()),
    );

    // 目印（EP-1）: しおり・ふせんは現在ページ、本棚はこのブックに対する付け外し
    const marks = this.group();
    if (ui.marks) {
      marks.append(
        this.button('bookmark', () => this.api.toggleMark('bookmark')),
        this.button('sticky', () => this.api.toggleMark('sticky')),
        this.button('shelf', () => this.api.toggleMark('shelf')),
      );
    }

    // いいね（EP-1 公開データ）は目印と同じ「このブックへの操作」なので同じ群に置く
    if (ui.comments) marks.append(this.likeButton());

    const panels = this.group();
    if (ui.marks) panels.append(this.button('marks', () => this.onTogglePanel('marks')));
    if (ui.comments) panels.append(this.button('comments', () => this.onTogglePanel('comments')));
    if (ui.search) panels.append(this.button('search', () => this.onTogglePanel('search')));
    if (ui.share) panels.append(this.button('share', () => this.onTogglePanel('share')));
    if (ui.thumbnails) panels.append(this.button('thumbnails', () => this.onTogglePanel('thumbnails')));
    if (ui.outline) panels.append(this.button('outline', () => this.onTogglePanel('outline')));
    if (ui.help) panels.append(this.button('help', () => this.onTogglePanel('help')));
    // 読み上げ（M11）。口を持たない環境では Shell がパネルを登録しないので、
    // ここも同じ条件で出さない（押しても何も起きないボタンを残さない）。
    if (ui.speech) panels.append(this.button('speech', () => this.onTogglePanel('speech')));

    const view = this.group();
    // 原本の配布（FR-DL-01・FR-PRINT-01）は、配信モードと PDF の権限フラグが分かるのが
    // 読み込み後なので、ここでは器だけ作り sync() で出し入れする。
    if (ui.download) view.append(this.button('download', () => this.api.download()));
    if (ui.print) view.append(this.button('print', () => this.api.print()));
    view.append(
      this.button('sound', () => this.api.toggleSound()),
      this.button('lightbox', () => this.api.toggleLightbox()),
      this.button('fullscreen', () => this.api.toggleFullscreen()),
    );

    this.el.append(nav, page, nav2, zoom, marks, panels, view);

    // APG の toolbar パターン（M8c-2 手順 5）。ツールバー全体で Tab の止まり木は
    // 1 つだけにし、中の移動は左右矢印と Home/End で行う。操作子が十数個あるのに
    // Tab を全部踏ませると、誌面へ辿り着くまでが遠すぎるため。
    registry.listen(this.el, 'keydown', (event) => this.onKeydown(event as KeyboardEvent));
    // 最後に触った操作子を止まり木として覚える（Tab で戻ったとき同じ場所に着く）
    registry.listen(this.el, 'focusin', (event) => this.roving(event.target as HTMLElement));
    this.roving();
  }

  /**
   * 矢印と Home/End で操作子の間を移動する。
   *
   * 文字入力欄の中ではカーソル移動を優先するが、**カーソルが端にあるときは譲らない** —
   * 一律に譲ると、止まり木が入力欄になった瞬間に他の操作子へ行く手段が無くなる
   * （1 ページ目は「最初へ」「前へ」が押せないため、初期の止まり木が入力欄になる）。
   * 実機確認でこの行き止まりが見つかった。テキスト編集の慣習どおり、左端の ← と
   * 右端の → だけを移動に使う。
   */
  private onKeydown(event: KeyboardEvent): void {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (target.tagName === 'INPUT' && !this.atEdge(target as HTMLInputElement, event.key)) return;

    const items = this.items();
    const index = items.indexOf(target);
    if (index < 0) return;

    let next: HTMLElement | undefined;
    if (event.key === 'ArrowRight') next = items[(index + 1) % items.length];
    else if (event.key === 'ArrowLeft') next = items[(index - 1 + items.length) % items.length];
    else if (event.key === 'Home') next = items[0];
    else if (event.key === 'End') next = items[items.length - 1];
    else return;

    // 引き取ったことを preventDefault で示す。誌面側のキー割当（ui/input.ts）は
    // defaultPrevented を見て手を出さない＝ツールバーの中で矢印がめくりにならない。
    event.preventDefault();
    next?.focus();
  }

  /**
   * 入力欄の中で、そのキーがカーソル移動に使われない位置か。
   * 左端での ← と右端での → は移動に使えると判断する。Home/End は入力欄では
   * 行頭・行末へのカーソル移動として使うので、常に譲る（false）。
   * 選択範囲があるあいだは編集中とみなして譲る。
   */
  private atEdge(input: HTMLInputElement, key: string): boolean {
    const start = input.selectionStart;
    const end = input.selectionEnd;
    if (start === null || end === null || start !== end) return false;
    if (key === 'ArrowLeft') return start === 0;
    if (key === 'ArrowRight') return start === input.value.length;
    return false;
  }

  /** 矢印で行き来する操作子（表示順・押せないものは飛ばす）。 */
  private items(): HTMLElement[] {
    const all = [...this.el.querySelectorAll<HTMLButtonElement | HTMLInputElement>('button, input')];
    return all.filter((item) => !item.disabled);
  }

  /** 止まり木を 1 つに保つ。`active` を渡すとそれを止まり木にする。 */
  private roving(active?: HTMLElement): void {
    const items = this.items();
    if (items.length === 0) return;
    const current =
      active && items.includes(active)
        ? active
        : (items.find((item) => item.getAttribute('tabindex') === '0') ?? items[0]);
    // 押せなくなったものも含めて全部書き換える。押せる要素だけを回すと、
    // 直前まで止まり木だったボタンが disabled になったとき tabindex="0" が
    // 残り、止まり木が 2 つになる（1 ページ目で「先頭へ」が無効になる場面）。
    for (const item of this.el.querySelectorAll<HTMLElement>('button, input')) {
      item.tabIndex = item === current ? 0 : -1;
    }
  }

  /** ボタンを追加する（利用者拡張・FR-UI-03）。 */
  addControl(def: ControlDef): void {
    const button = this.doc.createElement('button');
    button.type = 'button';
    button.className = `${cls('button')} ${cls(`button--${def.id}`)}`;
    button.setAttribute('aria-label', def.label);
    button.title = def.label;
    // アイコンは HTML として解釈しない（NFR-SEC-01）。ここは外部から与えられた文字列が
    // 唯一 innerHTML へ入る経路だったため、Node はそのまま置き文字列は文字として扱う。
    if (isNode(def.icon)) {
      button.appendChild(def.icon);
    } else {
      button.textContent = def.icon !== undefined && def.icon !== '' ? def.icon : def.label.slice(0, 1);
    }
    this.registry.listen(button, 'click', () => def.onClick(this.api));
    this.buttons.set(def.id, button);
    this.el.appendChild(button);
    // 追加ボタンも同じ止まり木の輪に入れる（Tab の順路を増やさない）
    this.roving();
  }

  /** 状態をボタン・入力欄へ反映する。 */
  update(state: ViewerState, openPanel: string | null): void {
    this.pageInput.value = state.label;
    this.pageTotal.textContent = this.translate('page.total', { total: state.pageCount });
    this.pageInput.setAttribute('aria-label', this.translate('toolbar.pageInput'));

    this.setDisabled('first', state.page <= 1);
    this.setDisabled('prev', state.page <= 1);
    this.setDisabled('next', state.page >= state.pageCount);
    this.setDisabled('last', state.page >= state.pageCount);
    this.setDisabled('zoomOut', state.zoom <= 1);

    this.setAvailable('download', state.canDownload);
    this.setAvailable('print', state.canPrint);
    this.setPressed('fullscreen', state.fullscreen);
    this.setPressed('sound', state.sound);
    this.setSoundIcon(state.sound);
    for (const [type, on] of Object.entries(state.marks)) {
      this.setPressed(type, on);
    }
    for (const id of ['marks', 'comments', 'search', 'share', 'thumbnails', 'outline', 'help', 'speech']) {
      this.setPressed(id, openPanel === id);
    }
    this.updateLike(state);
    // 押せなくなったボタンが止まり木のままだとツールバーへ入れなくなるので付け直す
    this.roving();
  }

  dispose(): void {
    this.el.remove();
  }

  private group(): HTMLDivElement {
    const group = this.doc.createElement('div');
    group.className = cls('toolbar-group');
    return group;
  }

  private button(id: string, onClick: () => void): HTMLButtonElement {
    const button = this.doc.createElement('button');
    button.type = 'button';
    button.className = `${cls('button')} ${cls(`button--${id}`)}`;
    const label = this.translate(`toolbar.${id}`);
    button.setAttribute('aria-label', label);
    button.title = label;
    button.innerHTML = ICONS[id] ?? '';
    this.registry.listen(button, 'click', onClick);
    this.buttons.set(id, button);
    return button;
  }

  private createPageInput(): HTMLInputElement {
    const input = this.doc.createElement('input');
    input.type = 'text';
    input.inputMode = 'numeric';
    input.className = cls('page-input');
    input.autocomplete = 'off';
    // name が無い欄はブラウザの自動入力ヒューリスティクスが扱えず、DevTools が
    // 「A form field element should have an id or name attribute」を出す。
    // id は 1 ページに複数のビューワを置くと衝突するので name だけを付ける。
    input.name = 'page';
    // 名前は**生成時から**付ける。sync() でも付け直すが（言語切替に追随するため）、
    // それだけだと初回同期までの間この欄に名前が無い。読み込み中でもツールバーは
    // DOM にあるので、その隙に焦点が入ると「編集テキスト」としか伝わらない
    // （M10 手順 12 の /view ページ全体走査で axe の label 違反として検出した）。
    input.setAttribute('aria-label', this.translate('toolbar.pageInput'));

    const commit = () => {
      const value = input.value.trim();
      if (value) this.api.goto(value);
    };
    this.registry.listen(input, 'keydown', (event) => {
      const keyboard = event as KeyboardEvent;
      if (keyboard.key !== 'Enter') return;
      keyboard.preventDefault();
      commit();
    });
    this.registry.listen(input, 'blur', commit);
    return input;
  }

  private setDisabled(id: string, disabled: boolean): void {
    const button = this.buttons.get(id);
    if (button) button.disabled = disabled;
  }

  /** 出せない操作子は隠す（隠れている間はロービング tabindex の対象からも外れる）。 */
  private setAvailable(id: string, available: boolean): void {
    const button = this.buttons.get(id);
    if (button) button.hidden = !available;
  }

  private setPressed(id: string, pressed: boolean): void {
    this.buttons.get(id)?.setAttribute('aria-pressed', String(pressed));
  }

  /** いいねボタン（アイコンの隣に件数を出す）。 */
  private likeButton(): HTMLButtonElement {
    const button = this.button('like', () => this.api.toggleLike());
    this.likeCount = this.doc.createElement('span');
    this.likeCount.className = cls('like-count');
    button.appendChild(this.likeCount);
    return button;
  }

  /** 件数と押下状態を反映する。未ログインでは押せない（理由をラベルに出す）。 */
  private updateLike(state: ViewerState): void {
    if (!this.likeCount) return;
    this.likeCount.textContent = state.likes > 0 ? String(state.likes) : '';
    this.setPressed('like', state.liked);
    this.setDisabled('like', !state.signedIn);
    const button = this.buttons.get('like');
    if (!button) return;
    // 件数が見えているときは、その数字を読み上げ名にも入れる（WCAG 2.5.3 Label in Name）。
    // 見えている文字が名前に無いと、音声操作で「いいね 12」と言っても押せない。
    const key = state.signedIn ? 'toolbar.like' : 'toolbar.likeSignIn';
    const label =
      state.likes > 0
        ? this.translate(`${key}Count`, { count: state.likes })
        : this.translate(key);
    button.setAttribute('aria-label', label);
    button.title = label;
  }

  /** 音の状態はアイコンとラベルの両方に出す（押下状態だけに頼らない）。 */
  private setSoundIcon(on: boolean): void {
    const button = this.buttons.get('sound');
    if (!button) return;
    button.innerHTML = ICONS[on ? 'sound' : 'soundOff'] ?? '';
    const label = this.translate(on ? 'toolbar.soundOff' : 'toolbar.sound');
    button.setAttribute('aria-label', label);
    button.title = label;
  }
}
